(() => {
  'use strict';
  /* Shield of Light - story hero. SHIELD-CC-STORE, 2026-09-24.
     1. Was capped at 30fps via `delta >= 1000/30`. Now paints every frame the
        browser offers, on delta time, so it is frame-rate independent.
     2. Called ctx.shadowBlur per particle, roughly 500 shadow ops a frame, which
        is why the cap existed. Replaced with one pre-rendered glow sprite drawn
        via drawImage with 'lighter' compositing.
     3. Scenes did not morph; they scattered out then in from a different random
        set, so transitions read as cuts. Points now travel directly from their
        position in shape A to shape B, with a per-point stagger.
     4. Was inert for 54% of each cycle. The hold now carries slow rotation,
        breath and a per-point shimmer.
     5. Added depth driving size, opacity and travel arc.
     PRESERVED: the three shapes, palette, 7.2s rhythm, prefers-reduced-motion,
     IntersectionObserver and visibility pausing, ResizeObserver, Shopify section
     load/unload, the window.SOLStoryHero interface, optional pause button. */
  if (window.SOLStoryHero) {
    window.SOLStoryHero.mount(document);
    return;
  }

  const controllers = new Map();
  const twoPi = Math.PI * 2;

  const SCENE = 7.2;
  const MORPH = 2.4;
  const STAGGER = 0.38;

  const circle = (x, y, r) => Array.from({ length: 17 }, (_, i) => [
    x + Math.cos((i / 16) * twoPi) * r,
    y + Math.sin((i / 16) * twoPi) * r
  ]);

  const paths = [
    [[[-0.82,-0.55],[-0.5,-0.65],[-0.23,-0.62],[0,-0.48],[0.23,-0.62],[0.5,-0.65],[0.82,-0.55],[0.82,0.61],[0.5,0.52],[0.23,0.54],[0,0.69],[-0.23,0.54],[-0.5,0.52],[-0.82,0.61],[-0.82,-0.55]],[[0,-0.48],[0,0.69]],[[-0.62,-0.29],[-0.39,-0.32],[-0.16,-0.23]],[[-0.62,-0.02],[-0.39,-0.05],[-0.16,0.04]],[[-0.62,0.25],[-0.39,0.22],[-0.16,0.31]],[[0.16,-0.23],[0.39,-0.32],[0.62,-0.29]],[[0.16,0.04],[0.39,-0.05],[0.62,-0.02]],[[0.16,0.31],[0.39,0.22],[0.62,0.25]]],
    [[[0,-0.85],[0.36,-0.68],[0.68,-0.58],[0.67,0.12],[0.56,0.4],[0.3,0.68],[0,0.86],[-0.3,0.68],[-0.56,0.4],[-0.67,0.12],[-0.68,-0.58],[-0.36,-0.68],[0,-0.85]],[[0,-0.46],[0,0.45]],[[-0.31,-0.12],[0.31,-0.12]]],
    [[[-0.9,0.1],[0.9,0.1],[0.62,0.57],[-0.57,0.57],[-0.9,0.1]],[[-0.61,0.1],[-0.61,-0.38],[0.56,-0.38],[0.56,0.1]],[[-0.75,-0.38],[-0.47,-0.67],[0.37,-0.67],[0.7,-0.38]],circle(-0.33,-0.13,0.08),circle(0,-0.13,0.08),circle(0.33,-0.13,0.08),[[-0.91,0.75],[-0.65,0.68],[-0.4,0.76],[-0.13,0.68],[0.13,0.76],[0.4,0.68],[0.65,0.76],[0.91,0.68]]]
  ];

  const easeOut = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -9 * t));
  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

  const seeded = (index, channel) => {
    const value = Math.sin((index + 1) * 12.9898 + channel * 78.233) * 43758.5453;
    return value - Math.floor(value);
  };

  const sample = (parts, count) => {
    const segments = [];
    let total = 0;
    for (const part of parts) {
      for (let i = 1; i < part.length; i += 1) {
        const a = part[i - 1];
        const b = part[i];
        const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
        segments.push({ a, b, start: total, length });
        total += length;
      }
    }
    return Array.from({ length: count }, (_, i) => {
      const distance = (i / count) * total;
      const segment = segments.find((item) => distance <= item.start + item.length) || segments[segments.length - 1];
      const t = segment.length ? (distance - segment.start) / segment.length : 0;
      return [
        segment.a[0] + (segment.b[0] - segment.a[0]) * t,
        segment.a[1] + (segment.b[1] - segment.a[1]) * t
      ];
    });
  };

  const makeSprite = (core) => {
    const size = 64;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grd.addColorStop(0, core);
    grd.addColorStop(0.18, 'rgba(255,243,205,0.92)');
    grd.addColorStop(0.46, 'rgba(240,201,112,0.42)');
    grd.addColorStop(1, 'rgba(201,162,76,0)');
    g.fillStyle = grd;
    g.beginPath();
    g.arc(size / 2, size / 2, size / 2, 0, twoPi);
    g.fill();
    return c;
  };

  let spriteGold = null;
  let spriteCream = null;

  function create(root) {
    if (controllers.has(root)) return;
    const canvas = root.querySelector('[data-sol-story-canvas]');
    const button = root.querySelector('[data-sol-pause]');
    if (!canvas || typeof canvas.getContext !== 'function') return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (!spriteGold) {
      spriteGold = makeSprite('rgba(255,250,235,1)');
      spriteCream = makeSprite('rgba(255,255,255,1)');
    }

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let manualPause = false;
    let visible = true;
    let frame = 0;
    let last = 0;
    let elapsed = 0;
    let width = 0;
    let height = 0;
    let pointCount = 0;
    let shapes = [];
    let depth = [];
    let phase = [];
    let order = [];
    let disposed = false;

    const animationEnabled = () => root.dataset.animate === 'true' && !motion.matches;
    const shouldRun = () => animationEnabled() && !manualPause && visible && !document.hidden && !disposed;

    function configurePoints() {
      const nextCount = width < 340 ? 260 : width < 500 ? 360 : 520;
      if (nextCount === pointCount) return;
      pointCount = nextCount;
      shapes = paths.map((path) => sample(path, pointCount));
      depth = new Float32Array(pointCount);
      phase = new Float32Array(pointCount);
      order = new Float32Array(pointCount);
      for (let i = 0; i < pointCount; i += 1) {
        depth[i] = 0.45 + seeded(i, 3) * 0.55;
        phase[i] = seeded(i, 4) * twoPi;
        order[i] = seeded(i, 5);
      }
    }

    function paint(staticFrame) {
      if (!width || !height || !pointCount || !shapes.length) return;
      ctx.clearRect(0, 0, width, height);

      const t = elapsed / 1000;
      const sceneIndex = staticFrame ? 0 : Math.floor(t / SCENE);
      const from = shapes[((sceneIndex % shapes.length) + shapes.length) % shapes.length];
      const to = shapes[(((sceneIndex + 1) % shapes.length) + shapes.length) % shapes.length];
      const inScene = staticFrame ? 0 : t % SCENE;
      const scale = Math.min(width * 0.43, height * 0.42);
      const cx = width / 2;
      const cy = height / 2;

      const spin = staticFrame ? 0 : Math.sin(t * 0.08) * 0.05;
      const breath = staticFrame ? 1 : 1 + Math.sin(t * 0.42) * 0.014;
      const cos = Math.cos(spin);
      const sin = Math.sin(spin);

      ctx.globalCompositeOperation = 'lighter';

      for (let i = 0; i < pointCount; i += 1) {
        const begin = order[i] * (MORPH * STAGGER);
        const local = clamp01((inScene - begin) / (MORPH - MORPH * STAGGER));
        const m = staticFrame ? 1 : easeOut(local);

        const ax = from[i][0];
        const ay = from[i][1];
        const bx = to[i][0];
        const by = to[i][1];

        const bow = Math.sin(m * Math.PI) * (0.06 + depth[i] * 0.06);
        const mx = ax + (bx - ax) * m;
        const my = ay + (by - ay) * m;
        const nx = mx + mx * bow;
        const ny = my + my * bow;

        const shimmer = staticFrame ? 0 : Math.sin(t * (0.9 + depth[i] * 1.4) + phase[i]) * 0.008;
        const px = (nx + shimmer) * breath;
        const py = (ny + shimmer) * breath;

        const rx = px * cos - py * sin;
        const ry = px * sin + py * cos;

        const size = Math.max(1.2, width / 300) * (0.7 + depth[i] * 1.15);
        const travelFade = staticFrame ? 1 : 0.62 + Math.cos((m - 0.5) * Math.PI) * 0.38;
        let alpha = (0.3 + depth[i] * 0.62) * travelFade;
        if (alpha > 1) alpha = 1;

        const sprite = i % 9 === 0 ? spriteCream : spriteGold;
        const d = size * 3.2;
        ctx.globalAlpha = alpha;
        ctx.drawImage(sprite, cx + rx * scale - d / 2, cy + ry * scale - d / 2, d, d);
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    function tick(now) {
      frame = 0;
      if (!shouldRun()) {
        last = 0;
        return;
      }
      if (!last) last = now;
      elapsed += Math.min(now - last, 64);
      last = now;
      paint(false);
      frame = requestAnimationFrame(tick);
    }

    function sync() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      last = 0;
      if (button) {
        button.hidden = !animationEnabled();
        button.setAttribute('aria-pressed', String(manualPause));
        if (button.dataset.playLabel && button.dataset.pauseLabel) {
          button.textContent = manualPause ? button.dataset.playLabel : button.dataset.pauseLabel;
        }
      }
      if (!animationEnabled()) {
        paint(true);
        return;
      }
      if (manualPause || !visible || document.hidden) {
        paint(false);
        return;
      }
      frame = requestAnimationFrame(tick);
    }

    function resize() {
      const box = canvas.getBoundingClientRect();
      width = box.width;
      height = box.height;
      if (!width || !height) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.75);
      canvas.width = Math.max(1, Math.round(width * ratio));
      canvas.height = Math.max(1, Math.round(height * ratio));
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      configurePoints();
      paint(!animationEnabled());
    }

    const click = () => {
      manualPause = !manualPause;
      sync();
    };

    if (button) button.addEventListener('click', click);
    if (motion.addEventListener) motion.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);

    const observer = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => {
          visible = entries[0].isIntersecting;
          sync();
        }, { threshold: 0.02 })
      : null;
    if (observer) observer.observe(root);

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
    if (resizeObserver) resizeObserver.observe(canvas);
    else window.addEventListener('resize', resize, { passive: true });

    resize();
    canvas.parentElement.classList.add('is-ready');
    sync();

    controllers.set(root, () => {
      disposed = true;
      if (frame) cancelAnimationFrame(frame);
      if (button) button.removeEventListener('click', click);
      if (motion.removeEventListener) motion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
      if (observer) observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener('resize', resize);
      controllers.delete(root);
    });
  }

  function mount(parent) {
    if (parent.matches && parent.matches('[data-sol-story-hero]')) create(parent);
    parent.querySelectorAll('[data-sol-story-hero]').forEach(create);
  }

  window.SOLStoryHero = { mount };
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, dispose] of controllers) {
      if (event.target.contains(root) || event.target === root) dispose();
    }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  } else {
    mount(document);
  }
})();
